import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import PasswordStrengthMeter from '../components/common/PasswordStrengthMeter';
import { Scale, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', password: '', confirm_password: '' });

  useEffect(() => {
    api.get(`/auth/invite/${token}`)
      .then(data => { setInvite(data); setLoading(false); })
      .catch(err => { setError(err?.response?.data?.error || 'Invalid invitation'); setLoading(false); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      const result = await api.post('/auth/accept-invite', { token, ...form });
      login(result.token, result.user, result.refresh_token);
      toast.success('Welcome to the team!');
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to accept invitation');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--zeus-black)' }}><div className="spinner" /></div>;

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--zeus-black)', padding: '24px' }}>
      <div className="login-card" style={{ textAlign: 'center' }}>
        <div className="logo-icon" style={{ margin: '0 auto 16px' }}><Scale size={28} /></div>
        <h2 style={{ fontFamily: 'var(--font-display)', marginBottom: '12px' }}>Invitation Error</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>{error}</p>
        <button className="btn btn-primary" onClick={() => navigate('/login')}>Go to Login</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--zeus-black)', padding: '24px' }}>
      <div className="login-card" style={{ maxWidth: '440px', textAlign: 'left' }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div className="logo-icon" style={{ margin: '0 auto 12px' }}><Scale size={28} /></div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem' }}>Zeus<span style={{ color: 'var(--zeus-gold)' }}>Law</span></h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
            You've been invited to join <strong style={{ color: 'var(--zeus-gold)' }}>{invite?.firm_name}</strong> as <strong>{invite?.role}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" value={invite?.email || ''} readOnly style={{ opacity: 0.6 }} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input className="form-input" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name *</label>
              <input className="form-input" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Password *</label>
              <input className="form-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required minLength={12} />
              <PasswordStrengthMeter password={form.password} />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm *</label>
              <input className="form-input" type="password" value={form.confirm_password} onChange={e => setForm({ ...form, confirm_password: e.target.value })} required />
            </div>
          </div>
          <button className="btn btn-primary btn-lg" type="submit" disabled={submitting} style={{ width: '100%', justifyContent: 'center', marginTop: '8px' }}>
            {submitting ? 'Joining...' : 'Join Team'} {!submitting && <ArrowRight size={18} />}
          </button>
        </form>
      </div>
    </div>
  );
}
